import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import TitledBlock from '../../components/TitledBlock';
import CardBox from '../../components/CardBox';
import EditCardButtons from '../../components/EditComponents/EditCardButtons';
import DisablingCover from '../../components/DisablingCover';
import { PRIVACY_ATTRIBUTE_IDS, TP } from '../../constants/consts';
import { getOption } from '../../utils/common';
import { EDITING_ENTITY_TYPES } from '../../utils/editCommon';
import { entityFields } from './constants';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    padding: theme.spacing(2),
    position: 'relative',
  },
  flexContainer: {
    display: 'flex',
    flexGrow: 1,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  column: {
    width: '25%',
    padding: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  cell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardButtons: {
    justifyContent: 'flex-end',
    maxWidth: 300,
    [theme.breakpoints.down('md')]: {
      flexBasis: 0,
      justifyContent: 'center',
    },
  },
  titleClass: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StaffCard = ({ staffMember, privacyOptions, isLoading, onDelete, onEdit, onPrivacyChange, type }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const staffEntity = {
    name: get(staffMember, entityFields[type]?.name),
    profession: get(staffMember, entityFields[type]?.employeeProfession),
    email: get(staffMember, entityFields[type]?.email),
    phones: get(staffMember, entityFields[type]?.phones),
  };

  return (
    <CardBox className={classes.root} key={`edit-staf-${staffMember.id}`}>
      <div className={classes.flexContainer}>
        <TitledBlock
          title={t(`${TP}.FN_NAME`)}
          className={classnames(classes.column, classes.bold)}
          contentClass={classes.cell}
          titleClass={classes.titleClass}
        >
          {staffEntity.name?.name}
        </TitledBlock>
        <TitledBlock
          title={t(`${TP}.m_JOBTITLE`)}
          className={classes.column}
          contentClass={classes.cell}
          titleClass={classes.titleClass}
        >
          {staffEntity.profession?.name}
        </TitledBlock>
        <TitledBlock
          title={t(`${TP}.m_ARTIST_PHONE`)}
          className={classes.column}
          contentClass={classes.cell}
          titleClass={classes.titleClass}
        >
          {staffEntity.phones?.map(p => (
            <div key={p}>
              {p}
              <br />
            </div>
          ))}
        </TitledBlock>
        <TitledBlock
          title={t(`${TP}.m_EMAIL`)}
          className={classes.column}
          contentClass={classes.cell}
          titleClass={classes.titleClass}
        >
          {staffEntity.email}
        </TitledBlock>
      </div>
      <EditCardButtons
        editClick={onEdit}
        visibilityClick={v => onPrivacyChange(v, staffMember)}
        editButtonName={`${TP}.FN_EDIT`}
        privacyOptions={privacyOptions}
        value={
          getOption(get(staffMember, 'privacyAttributes[0]')) ||
          privacyOptions?.find(el => el.value === PRIVACY_ATTRIBUTE_IDS.PRO)
        }
        className={classes.cardButtons}
        onDelete={onDelete}
        deleteButtonText={t(`${TP}.FN_REMOVE`)}
      />
      {isLoading && <DisablingCover />}
    </CardBox>
  );
};

StaffCard.propTypes = {
  staffMember: PropTypes.object.isRequired,
  type: PropTypes.oneOf([EDITING_ENTITY_TYPES.FESTIVAL, EDITING_ENTITY_TYPES.COMPANY]),
};

StaffCard.defaultProps = {};

export default StaffCard;
